import { apiGet, apiPost, apiDelete } from './client';
import type { AiAgentKnowledge, OperationResult, PagedItemsResult } from './types';

export async function getKnowledgeForAgent(agentId: number, pageSize = 100): Promise<AiAgentKnowledge[]> {
  const result = await apiGet<OperationResult<PagedItemsResult<AiAgentKnowledge>>>(
    '/api/AiAgentKnowledge/Search',
    { aiAgentId: agentId, pageNo: 1, pageSize }
  );
  if (!result.isSuccess || !result.data) {
    throw new Error(result.message || 'Failed to load knowledge sources');
  }
  return result.data.data ?? [];
}

/**
 * Link an existing Knowledge base to an agent. The backend
 * (AiAgentKnowledgeController.Add) validates that:
 *  - the agent belongs to the caller's merchant,
 *  - the KB belongs to the caller's merchant,
 *  - the (agentId, knowledgeId) pair is not already linked.
 *
 * If any check fails, the response is `isSuccess=false` (not a thrown HTTP
 * error), so we surface the message verbatim — the most common case is
 * "already linked", which the UI presents as a friendly "this knowledge
 * is already attached to the agent" rather than a stack trace.
 */
export async function linkKnowledgeToAgent(agentId: number, knowledgeId: number): Promise<void> {
  const result = await apiPost<OperationResult<unknown>>('/api/AiAgentKnowledge/Add', {
    aiAgentId: agentId,
    knowledgeId,
  });
  if (!result.isSuccess) {
    throw new Error(result.message || 'Failed to link knowledge to agent');
  }
}

/**
 * Unlink a Knowledge base from an agent. `linkId` is the row id of the
 * AiAgentKnowledge join entry (not the underlying Knowledge id) — the same
 * `id` shown in the agent detail's Knowledge table.
 */
export async function unlinkKnowledgeFromAgent(linkId: number): Promise<void> {
  const result = await apiDelete<OperationResult<unknown>>(`/api/AiAgentKnowledge/Remove/${linkId}`);
  if (!result.isSuccess) {
    throw new Error(result.message || 'Failed to unlink knowledge from agent');
  }
}

// ── Inverse direction: list agents linked to a given KB ────────────────────
// Used by the KB detail page's "Linked agents" surface. The backend's
// AiAgentKnowledgeController.Search accepts EITHER aiAgentId OR knowledgeId
// (validated to be exactly one); when called with knowledgeId, each row's
// `agent` nav is populated by the repository projection and `knowledge` is
// the same KB (redundant for this caller but harmless).

export async function getAgentsForKnowledge(knowledgeId: number, pageSize = 100): Promise<AiAgentKnowledge[]> {
  const result = await apiGet<OperationResult<PagedItemsResult<AiAgentKnowledge>>>(
    '/api/AiAgentKnowledge/Search',
    { knowledgeId, pageNo: 1, pageSize }
  );
  if (!result.isSuccess || !result.data) {
    throw new Error(result.message || 'Failed to load linked agents');
  }
  return result.data.data ?? [];
}

/**
 * Link an existing agent to a KB. Same endpoint as `linkKnowledgeToAgent`;
 * argument order is swapped to match the calling surface (the KB detail page
 * is the agent picker, not the KB picker). The backend doesn't care which
 * "side" initiated — it writes the same ai.AiAgentKnowledge row either way.
 */
export async function linkAgentToKnowledge(knowledgeId: number, agentId: number): Promise<void> {
  return linkKnowledgeToAgent(agentId, knowledgeId);
}

/**
 * Unlink an agent from a KB. Identical wire call to `unlinkKnowledgeFromAgent`
 * — both routes through DELETE /api/AiAgentKnowledge/Remove/{linkId} — but
 * named for the KB-side surface so call sites read naturally.
 */
export async function unlinkAgentFromKnowledge(linkId: number): Promise<void> {
  return unlinkKnowledgeFromAgent(linkId);
}
